import { inject } from '@angular/core';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, catchError, pipe, switchMap, tap } from 'rxjs';
import { OrderSyncContext } from '@shared/platform';
import { OrderApi } from '../api/order.api';
import type { BuildLimitOrderDto, BuildLimitOrderResult, LimitOrder } from './order.schema';

export const OrderStore = signalStore(
  { providedIn: 'root' },
  withState({ orders: [] as LimitOrder[], loading: false }),
  withMethods((store, api = inject(OrderApi)) => {
    // Extension-only: its worker polls fills and wants to hear about a change
    // now, not on its next alarm. Null on the web build.
    const sync = inject(OrderSyncContext, { optional: true });
    const reload = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { loading: true })),
        switchMap(() =>
          api.list$().pipe(
            tap(orders => patchState(store, { orders, loading: false })),
            catchError(() => {
              patchState(store, { loading: false });
              return EMPTY;
            }),
          ),
        ),
      ),
    );
    return {
      reload,
      reset(): void {
        patchState(store, { orders: [], loading: false });
      },
      async create(dto: BuildLimitOrderDto, freshToken?: string): Promise<BuildLimitOrderResult> {
        const res = await api.create(dto, freshToken);
        reload();
        sync?.orderChanged();
        return res;
      },
      async cancel(id: string): Promise<void> {
        await api.cancel(id);
        reload();
        sync?.orderChanged();
      },
      async submit(id: string, freshToken: string): Promise<LimitOrder> {
        const res = await api.submit(id, freshToken);
        reload();
        sync?.orderChanged();
        return res;
      },
    };
  }),
  withHooks({ onInit: ({ reload }) => reload() }),
);
